import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/store';

export type AppTheme = 'light' | 'dark';

export interface PwaMode {
  isPWA: boolean;
  isInstalled: boolean;
  displayMode: string;
  isStandalone: boolean;
  isBrowser: boolean;
}

interface AppState {
  theme: string;
  navigatorOn: boolean;
  navigatorBackOff: boolean;
  openedModals: string[];
  pwaMode: PwaMode;
  contentHeight: string;
}

const initialState: AppState = {
  theme: localStorage.getItem('theme') ?? 'light',
  navigatorOn: true,
  navigatorBackOff: false,
  openedModals: [],
  pwaMode: {
    isPWA: false,
    isInstalled: false,
    displayMode: 'browser',
    isStandalone: false,
    isBrowser: true,
  },
  contentHeight: 'auto',
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateThemeState: state => {
      state.theme = localStorage.getItem('theme') ?? 'light';
    },
    addModal: (state, action: PayloadAction<string>) => {
      state.openedModals.unshift(action.payload);
    },
    removeModal: (state, action: PayloadAction<string>) => {
      state.openedModals = state.openedModals.filter(modal => modal !== action.payload);
    },
    hideNavigator: state => {
      state.navigatorOn = false;
    },
    showNavigator: state => {
      state.navigatorOn = true;
    },
    setPwaMode: (state, action: PayloadAction<PwaMode>) => {
      state.pwaMode = action.payload;
    },
    setNavigatorBackOff: (state, action: PayloadAction<boolean>) => {
      state.navigatorBackOff = action.payload;
    },
    setContentHeight: (state, action: PayloadAction<string>) => {
      state.contentHeight = action.payload;
    },
  },
});

export const {
  updateThemeState,
  addModal,
  removeModal,
  hideNavigator,
  showNavigator,
  setPwaMode,
  setNavigatorBackOff,
  setContentHeight,
} = appSlice.actions;

export const selectNavigatorState = (state: RootState) => state.app.navigatorOn;
export const selectNavigatorBackState = (state: RootState) => state.app.navigatorBackOff;
export const selectPwaModeState = (state: RootState) => state.app.pwaMode;
export const selectContentHeight = (state: RootState) => state.app.contentHeight;
export const selectTheme = (state: RootState) => state.app.theme as AppTheme;

export default appSlice.reducer;
